import { useRef, useState } from "react";
import FsLightbox from "fslightbox-react";
import sx from "./slider.module.css";
import { getSizes, getSrcSet } from "@/utils/media";

type ImageObject = {
	titel: string;
	omschrijving: string;
	mimetype: string;
	wijzigingstijdstip: string;
	link?: string;
	sizes?: [];
	volgnummer: number;
	vrijgave: boolean;
	soort: string;
};

type Props = {
	images: ImageObject[];
};

const ImageSnapSlider: React.FC<React.PropsWithChildren<Props>> = ({
	images,
}) => {
	const imageSliderRef = useRef(null);
	const [lightboxController, setLightboxController] = useState({
		toggler: false,
		slide: 1,
	});

	const scrollElementByAmount = (
		element: HTMLElement,
		amount: number,
	): void => {
		element.scrollBy({ left: amount, behavior: "smooth" });
	};

	const handleSlideChange = (forward: boolean): void => {
		if (forward) {
			scrollElementByAmount(
				imageSliderRef.current,
				imageSliderRef.current.getBoundingClientRect().width,
			);
		} else {
			scrollElementByAmount(
				imageSliderRef.current,
				-imageSliderRef.current.getBoundingClientRect().width,
			);
		}
	};

	function openLightboxOnSlide(number) {
		setLightboxController({
			toggler: !lightboxController.toggler,
			slide: number,
		});
	}

	function getImage(image: ImageObject) {
		if (!image.sizes) {
			if (image.link) {
				const url = new URL(image.link);
				url.searchParams.set("resize", "4");
				return url.toString();
			} else {
				return "";
			}
		}

		return Object.values(image.sizes).slice(-1)[0];
	}

	return (
		<div className="slider flex flex-col">
			<div ref={imageSliderRef} className={sx.imageSlider}>
				{images.map((image, index) => (
					<a
						key={index}
						className={sx.imageAnchor}
						onClick={() => openLightboxOnSlide(index + 1)}
						role="button"
						tabIndex={0}
					>
						<img
							src={getImage(image)}
							className={sx.image}
							{...(image.sizes && { sizes: getSizes(image.sizes) })}
							{...(image.sizes && { srcSet: getSrcSet(image.sizes) })}
							width={808}
							height={538}
						/>
					</a>
				))}
			</div>
			{images.length > 1 ? (
				<div className="mt-2 flex flex-row justify-center space-x-6">
					<button
						className="bg-primary rounded-full p-3 shadow"
						type="button"
						onClick={() => handleSlideChange(false)}
					>
						<svg
							className="h-6 w-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="M15 19l-7-7 7-7"
							></path>
						</svg>
					</button>
					<button
						className="bg-primary rounded-full p-3 shadow"
						type="button"
						onClick={() => handleSlideChange(true)}
					>
						<svg
							className="h-6 w-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="M9 5l7 7-7 7"
							></path>
						</svg>
					</button>
				</div>
			) : null}
			<FsLightbox
				toggler={lightboxController.toggler}
				sources={images.flatMap((source) => {
					if (!source.sizes) {
						if (source.link) {
							const url = new URL(source.link);
							url.searchParams.set("resize", "4");
							return [url.toString()];
						} else {
							return [];
						}
					}

					return Object.values(source.sizes).slice(-1)[0];
				})}
				type="image"
				slide={lightboxController.slide}
			/>
		</div>
	);
};

export default ImageSnapSlider;
